import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Download, Loader2, Shuffle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  searchByFame,
  getTune,
  getCachedAiAnalysis,
  adminApi,
  getMeta,
  searchByName,
  searchByNotes,
  searchByLink,
  getRecordingTrack,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { buildAbc } from "../lib/abcUtils";
import { SIZES, nodeToPng, buildZip, captionFile } from "../lib/socialExport";
import { FEATURES, BADGES } from "../lib/socialFeatures";
import { ZOOM_STOPS } from "../lib/abcLayout";
import {
  CoverCard,
  TuneTitleCard,
  NotationCard,
  AbcCard,
  AnalysisCard,
  FeatureCoverCard,
  FeatureSlideCard,
} from "../components/social/SocialCards";
import { LogoPackPanel } from "../components/social/LogoPackPanel";

const POST = SIZES.portrait;
const SCALE = 0.22;
const TABS = [
  ["cover", "Cover pack"],
  ["logo", "Logo pack"],
  ["tune", "Tune post"],
  ["feature", "Feature post"],
];

const Btn = ({ children, onClick, disabled, testid, primary }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold transition-transform hover:scale-105 disabled:opacity-50 ${
      primary ? "bg-[#8a2be2] text-white" : "border-2 border-[#2d2824] text-[#2d2824]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const Pill = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`rounded-full border-2 px-4 py-2 text-sm font-extrabold ${
      active ? "border-[#2d2824] bg-[#2d2824] text-white" : "border-[#e5e0d8] bg-white text-[#2d2824]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

export default function SocialStudioPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const pickRef = useRef(0);
  const [tab, setTab] = useState("cover");
  const [busy, setBusy] = useState("");
  const [badge, setBadge] = useState("full");
  const [dark, setDark] = useState(true);
  const [text1, setText1] = useState(() => localStorage.getItem("foinn.social.text1") ?? "23,000 traditional tunes.");
  const [text2, setText2] = useState(() => localStorage.getItem("foinn.social.text2") ?? "Find the one you half remember.");
  useEffect(() => {
    localStorage.setItem("foinn.social.text1", text1);
    localStorage.setItem("foinn.social.text2", text2);
  }, [text1, text2]);
  const [copy, setCopy] = useState(null);

  // tune mode
  const [fame, setFame] = useState(85);
  const [tune, setTune] = useState(null);
  const [settingIdx, setSettingIdx] = useState(0);
  const [analysis, setAnalysis] = useState(null);

  // feature mode
  const [featureId, setFeatureId] = useState(FEATURES[0].id);
  useEffect(() => setCopy(null), [tab, featureId]);
  const feature = FEATURES.find((f) => f.id === featureId);
  const [demo, setDemo] = useState(null);
  const [zooms, setZooms] = useState({});

  // real search results for the "app screen" slides, fetched once
  useEffect(() => {
    if (tab !== "feature" || demo || !["name", "link", "fame"].includes(featureId)) return;
    setDemo({});
    (async () => {
      const safe = (p) => p.catch(() => null);
      const [meta, name, notes, link, fameRes] = await Promise.all([
        safe(getMeta()),
        safe(searchByName("cooley")),
        safe(searchByNotes("FAA BAA")),
        safe(searchByLink("https://tidal.com/track/215963022/u")),
        safe(searchByFame({ rare_famous: fame, rarely_often: fame })),
      ]);
      const rec = link?.link?.recording;
      const next = rec?.next ? await safe(getRecordingTrack(rec.id, rec.next)) : null;
      setDemo({
        types: meta?.types,
        keys: meta?.keys,
        name: name?.results,
        notes: notes?.results,
        link: link?.link,
        linkResults: link?.results,
        album: next
          ? { ...link.link, title: next.results.map((r) => r.name).join(" / "), recording: next.recording }
          : link?.link,
        albumResults: next?.results || link?.results,
        fame: fameRes?.results,
      });
    })();
  }, [tab, featureId, demo, fame]);

  const abc = useMemo(() => {
    if (!tune) return "";
    const st = tune.settings?.[settingIdx];
    return st
      ? buildAbc({ name: tune.name, type: tune.type, key: st.key, meter: st.meter, abc: st.abc })
      : "";
  }, [tune, settingIdx]);

  const pickTune = useCallback(async () => {
    const run = ++pickRef.current;
    setBusy("pick");
    try {
      const res = await searchByFame({ rare_famous: fame, rarely_often: fame });
      const list = res.results || [];
      if (!list.length) {
        toast.error("No tune at that fame level");
        return;
      }
      const hit = list[Math.floor(Math.random() * list.length)];
      const [full, cached] = await Promise.all([
        getTune(hit.tune_id),
        getCachedAiAnalysis(hit.tune_id).catch(() => null),
      ]);
      if (run !== pickRef.current) return; // a newer pick won
      setTune({ ...full, fame_score: hit.fame_score });
      setSettingIdx(0);
      setAnalysis(cached?.analysis || null);
      setCopy(null);
    } catch (e) {
      toast.error("Could not load a tune");
    } finally {
      if (run === pickRef.current) setBusy("");
    }
  }, [fame]);

  // a tune is needed by the feature slides too (real notation, not a mock)
  useEffect(() => {
    if (!tune) pickTune();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grab = async (id, filename) => {
    const node = document.querySelector(`[data-social-card="${id}"]`);
    if (!node) return null;
    return { filename, dataUrl: await nodeToPng(node) };
  };

  const writeCopy = async (kind, context) => {
    try {
      const res = await adminApi.socialCaption(kind, context);
      setCopy(res);
      return res;
    } catch {
      toast.error("Caption generation failed — exporting without copy");
      return null;
    }
  };

  const exportCover = async () => {
    setBusy("cover");
    try {
      const files = [];
      for (const [name, s] of Object.entries(SIZES)) {
        const f = await grab(`cover-${s.w}x${s.h}`, `foinn-cover-${name}-${s.w}x${s.h}.png`);
        if (f) files.push(f);
      }
      const c = await writeCopy(
        "brand cover",
        `A brand cover pack for Foinn.org. Badge style: ${BADGES[badge]}. The image shows the Foinn.org logo and the lines "${text1}" / "${text2}".`
      );
      await buildZip(`foinn-cover-pack-${badge}-${dark ? "dark" : "light"}`, files, { "caption.txt": captionFile(c) });
      toast.success(`${files.length} images exported`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  const exportTune = async () => {
    if (!tune) return;
    setBusy("tune");
    try {
      const files = await grabCards();
      const c = await writeCopy(
        "tune",
        `Tune: ${tune.name} (${tune.type}). Key: ${tune.settings?.[settingIdx]?.key}. Fame ${Math.round(
          tune.fame_score || fame
        )}/100. Settings: ${tune.settings?.length}. Recordings: ${tune.recordings_count}. ${
          analysis
            ? `Expert notes — composer: ${analysis.composer}; date: ${analysis.date}; region: ${
                analysis.region || analysis.origin
              }; story: ${(analysis.history || "").slice(0, 600)}`
            : "No expert analysis available."
        }`
      );
      await buildZip(
        `foinn-tune-${tune.tune_id}-v${settingIdx + 1}`,
        files,
        { "caption.txt": captionFile(c) }
      );
      toast.success(`${files.length} images exported`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  const exportFeature = async () => {
    setBusy("feature");
    try {
      const files = await grabCards();
      const c = await writeCopy(
        "feature",
        `Feature: ${feature.title} (${feature.tag}). What it does: ${feature.bullets.join(
          " · "
        )}. The carousel shows: ${feature.slides.map((s) => s.caption).join(" / ")}.`
      );
      await buildZip(`foinn-feature-${feature.id}-${dark ? "dark" : "light"}`, files, { "caption.txt": captionFile(c) });
      toast.success(`${files.length} images exported`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  // long tunes flow onto extra pages (same magnet layout as the PDF export)
  const [pageCounts, setPageCounts] = useState({});
  const pagesFor = useCallback(
    (zid) => (n) => setPageCounts((p) => (p[zid] === n ? p : { ...p, [zid]: n })),
    []
  );
  const zoomOf = (zid, def = 100) => zooms[zid] || def;
  // one card per page of a zoomable item
  const paged = (zid, def, make) => {
    const n = pageCounts[zid] || 1;
    return Array.from({ length: n }, (_, page) => ({
      zid: page === 0 ? zid : null,
      zdef: def,
      page,
      pages: n,
      node: make({ zoom: zoomOf(zid, def), page, pages: n, onPages: page === 0 ? pagesFor(zid) : undefined }),
    }));
  };

  const cards = useMemo(() => {
    if (tab === "cover") {
      return Object.values(SIZES).map((s) => ({
        w: s.w,
        h: s.h,
        node: <CoverCard w={s.w} h={s.h} badge={badge} dark={dark} text1={text1} text2={text2} />,
      }));
    }
    if (tab === "tune" && tune) {
      const list = [
        { fid: "tune-title", file: "1-title.png", node: <TuneTitleCard w={POST.w} h={POST.h} tune={tune} version={settingIdx + 1} fame={tune.fame_score} dark={dark} /> },
        ...paged("notation", 100, (fit) => (
          <NotationCard
            w={POST.w}
            h={POST.h}
            abc={abc}
            label={fit.pages > 1 ? `Standard notation · ${fit.page + 1}/${fit.pages}` : "Standard notation"}
            dark={dark}
            fit={fit}
          />
        )).map((c) => ({ ...c, fid: `notation-${c.page + 1}`, file: `2-notation-${c.page + 1}.png` })),
        ...paged("abc", 100, (fit) => <AbcCard w={POST.w} h={POST.h} abc={abc} dark={dark} fit={fit} />).map((c) => ({
          ...c,
          fid: `abc-${c.page + 1}`,
          file: `3-abc-${c.page + 1}.png`,
        })),
      ];
      if (analysis)
        list.push({ fid: "analysis", file: "4-expert-analysis.png", node: <AnalysisCard w={POST.w} h={POST.h} tune={tune} analysis={analysis} dark={dark} /> });
      return list.map((c) => ({ ...c, w: POST.w, h: POST.h }));
    }
    if (tab === "feature" && abc) {
      const slides = feature.slides.flatMap((slide, i) => {
        const make = (fit) => (
          <FeatureSlideCard
            index={i}
            w={POST.w}
            h={POST.h}
            feature={feature}
            slide={slide}
            abc={abc}
            analysis={analysis}
            dark={dark}
            fit={fit}
            demo={demo || {}}
            tune={tune}
          />
        );
        const fixed = !(["notation", "abc", "export", "export-landscape", "player"].includes(slide.kind) || slide.kind.startsWith("screen-"));
        const items = fixed ? [{ page: 0, node: make({}) }] : paged(`${feature.id}-${i}`, Math.round((slide.zoom || 1) * 100), make);
        return items.map((c) => ({ ...c, fid: `feature-${i}-${c.page}`, file: `${i + 2}-${slide.kind}${c.page ? `-${c.page + 1}` : ""}.png` }));
      });
      return [
        { fid: "feature-cover", file: "1-cover.png", node: <FeatureCoverCard w={POST.w} h={POST.h} feature={feature} dark={dark} /> },
        ...slides,
      ].map((c) => ({ ...c, w: POST.w, h: POST.h }));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, badge, dark, text1, text2, tune, settingIdx, abc, analysis, feature, zooms, demo, pageCounts]);

  const grabCards = async () => {
    const files = [];
    for (const c of cards) {
      // eslint-disable-next-line no-await-in-loop
      const f = c.fid && (await grab(c.fid, c.file));
      if (f) files.push(f);
    }
    return files;
  };

  return (
    <div className="min-h-screen bg-[#f7f3ec] px-4 py-6 sm:px-8" data-testid="social-studio">
      <div className="mx-auto max-w-5xl">
        {!loading && !user?.is_admin && (
          <p className="mb-4 rounded-2xl border-2 border-[#e11d48] bg-white p-4 text-sm font-bold text-[#e11d48]" data-testid="social-admin-only">
            Admins only — sign in with the admin account to generate social assets.
          </p>
        )}
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold"
          data-testid="social-back"
        >
          <ChevronLeft className="h-4 w-4" /> Admin
        </button>

        <h1 className="mt-4 font-serif text-4xl font-bold text-[#2d2824]">Generate for social</h1>
        <p className="mt-1 text-sm font-semibold text-[#6e655b]">
          Ready-to-post images in Foinn.org's own style, zipped with an AI-written caption.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Pill active={dark} onClick={() => setDark(true)} testid="theme-dark">
            Dark
          </Pill>
          <Pill active={!dark} onClick={() => setDark(false)} testid="theme-light">
            Light
          </Pill>
          <span className="mx-1 h-6 w-px bg-[#e5e0d8]" />
          {TABS.map(([id, label]) => (
            <Pill key={id} active={tab === id} onClick={() => setTab(id)} testid={`social-tab-${id}`}>
              {label}
            </Pill>
          ))}
        </div>

        <div className="mt-5 rounded-3xl border border-[#e5e0d8] bg-white p-5">
          {tab === "cover" && (
            <>
              <h2 className="text-xs font-extrabold uppercase tracking-widest text-[#6e655b]">
                Badge style
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(BADGES).map(([id, label]) => (
                  <Pill key={id} active={badge === id} onClick={() => setBadge(id)} testid={`badge-${id}`}>
                    {label}
                  </Pill>
                ))}
              </div>
              <h2 className="mt-4 flex items-center gap-3 text-xs font-extrabold uppercase tracking-widest text-[#6e655b]">
                Headline
                {(text1 || text2) && (
                  <button
                    onClick={() => {
                      setText1("");
                      setText2("");
                    }}
                    className="rounded-full border border-[#e5e0d8] px-2.5 py-0.5 text-[10px] normal-case tracking-normal text-[#2d2824] hover:bg-[#f3efea]"
                    data-testid="cover-clear-headline"
                  >
                    No headline
                  </button>
                )}
              </h2>
              <input
                value={text1}
                onChange={(e) => setText1(e.target.value)}
                maxLength={60}
                placeholder="Line 1 — leave both empty for a logo-only cover"
                className="mt-2 w-full rounded-xl border-2 border-[#e5e0d8] px-3 py-2 text-sm font-bold"
                data-testid="cover-text-1"
              />
              <input
                value={text2}
                onChange={(e) => setText2(e.target.value)}
                maxLength={60}
                placeholder="Line 2 (accent colour)"
                className="mt-2 w-full rounded-xl border-2 border-[#e5e0d8] px-3 py-2 text-sm font-bold"
                data-testid="cover-text-2"
              />
              <p className="mt-3 text-xs font-semibold text-[#6e655b]">
                Exports {Object.keys(SIZES).length} files:{" "}
                {Object.values(SIZES)
                  .map((s) => `${s.w}×${s.h}`)
                  .join(" · ")}
              </p>
              <div className="mt-4">
                <Btn onClick={exportCover} disabled={!!busy} primary testid="export-cover">
                  {busy === "cover" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export cover pack
                </Btn>
              </div>
            </>
          )}

          {tab === "logo" && <LogoPackPanel dark={dark} busy={busy} setBusy={setBusy} />}

          {tab === "tune" && (
            <>
              <h2 className="text-xs font-extrabold uppercase tracking-widest text-[#6e655b]">
                Fame level · {fame}
              </h2>
              <input
                type="range"
                min="0"
                max="100"
                value={fame}
                onChange={(e) => setFame(Number(e.target.value))}
                className="mt-2 w-full accent-[#8a2be2]"
                data-testid="social-fame-slider"
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Btn onClick={pickTune} disabled={!!busy} testid="social-pick-tune">
                  {busy === "pick" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
                  Random tune at this level
                </Btn>
                {tune && (
                  <span className="text-sm font-bold text-[#2d2824]" data-testid="social-tune-name">
                    {tune.name} · {tune.type} · fame {Math.round(tune.fame_score || 0)}
                  </span>
                )}
              </div>

              {tune?.settings?.length > 1 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="social-versions">
                  {tune.settings.map((st, i) => (
                    <Pill
                      key={st.setting_id}
                      active={settingIdx === i}
                      onClick={() => setSettingIdx(i)}
                      testid={`social-version-${i}`}
                    >
                      v{i + 1} · {st.key}
                    </Pill>
                  ))}
                </div>
              )}

              <p className="mt-3 text-xs font-semibold text-[#6e655b]">
                {pageCounts.notation || 1} notation page{(pageCounts.notation || 1) > 1 ? "s" : ""} ·{" "}
                {analysis ? "expert analysis included" : "no cached analysis for this tune"}
              </p>

              <div className="mt-4">
                <Btn onClick={exportTune} disabled={!!busy || !tune} primary testid="export-tune">
                  {busy === "tune" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export tune post
                </Btn>
              </div>
            </>
          )}

          {tab === "feature" && (
            <>
              <h2 className="text-xs font-extrabold uppercase tracking-widest text-[#6e655b]">
                Feature
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {FEATURES.map((f) => (
                  <Pill
                    key={f.id}
                    active={featureId === f.id}
                    onClick={() => setFeatureId(f.id)}
                    testid={`feature-${f.id}`}
                  >
                    {f.title}
                  </Pill>
                ))}
              </div>
              <p className="mt-3 text-xs font-semibold text-[#6e655b]">
                {feature.slides.length + 1} slides · real notation from {tune?.name || "a tune"}
              </p>
              <div className="mt-4">
                <Btn onClick={exportFeature} disabled={!!busy || !abc} primary testid="export-feature">
                  {busy === "feature" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export feature post
                </Btn>
              </div>
            </>
          )}
        </div>

        {copy && (
          <div className="mt-4 rounded-3xl border border-[#e5e0d8] bg-white p-5" data-testid="social-copy">
            <h2 className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-[#8a2be2]">
              <Sparkles className="h-4 w-4" /> Caption (also in the zip)
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-sm font-semibold text-[#2d2824]">
              {copy.caption}
            </p>
            <p className="mt-2 text-sm font-bold text-[#6e655b]">
              {(copy.hashtags || []).map((h) => `#${h}`).join(" ")}
            </p>
          </div>
        )}

        {/* every card, scaled down — this is also what gets rasterised */}
        {tab !== "logo" && (
        <div className="mt-6 rounded-3xl border border-[#e5e0d8] bg-white p-4">
          <h2 className="text-xs font-extrabold uppercase tracking-widest text-[#6e655b]">
            Preview · {cards.length} image{cards.length === 1 ? "" : "s"}
          </h2>
          <div className="mt-3 flex gap-4 overflow-x-auto pb-2" data-testid="social-preview">
            {cards.map((c, i) => (
              <div key={i} className="flex shrink-0 flex-col gap-2" style={{ width: c.w * SCALE }}>
                <div
                  style={{ width: c.w * SCALE, height: c.h * SCALE, overflow: "hidden" }}
                  className="rounded-xl border border-[#e5e0d8]"
                >
                  <div style={{ transform: `scale(${SCALE})`, transformOrigin: "top left" }}>{c.node}</div>
                </div>
                {c.zid && (
                  <label className="flex items-center gap-2 text-[11px] font-extrabold text-[#6e655b]">
                    Zoom
                    <input
                      type="range"
                      min="0"
                      max={ZOOM_STOPS.length - 1}
                      step="1"
                      value={Math.max(0, ZOOM_STOPS.indexOf(zoomOf(c.zid, c.zdef)))}
                      onChange={(e) => setZooms((z) => ({ ...z, [c.zid]: ZOOM_STOPS[Number(e.target.value)] }))}
                      className="min-w-0 flex-1 accent-[#8a2be2]"
                      data-testid={`social-zoom-${c.zid}`}
                    />
                    <span className="w-9 text-right">{zoomOf(c.zid, c.zdef)}%</span>
                  </label>
                )}
                {!c.zid && c.pages > 1 && (
                  <span className="text-[11px] font-extrabold text-[#8a2be2]">
                    page {c.page + 1}/{c.pages}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
